import React, { useEffect, useState } from 'react';
import { db, collection, onSnapshot, query, orderBy, handleFirestoreError, OperationType, limit } from '../firebase';
import { Task, Report, Client, UserProfile, CashRequisition, Notification } from '../types';
import { useAuth } from '../App';
import { USER_ROLES, ROLE_LABELS } from '../constants';
import { 
  Wrench, 
  FileText, 
  Building2, 
  Users, 
  ClipboardList, 
  TrendingUp,
  Clock,
  CheckCircle2,
  AlertCircle,
  Eye,
  Settings,
  FileDown,
  X as XIcon,
  ChevronRight,
  FileBarChart,
  DollarSign,
  Check,
  X,
  MessageSquare,
  Bell,
  Key
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { updateDoc, doc, Timestamp, where } from 'firebase/firestore';
import { toast } from 'sonner';
import { downloadFile } from '../lib/downloadHelper';
import { GenerateReportTrigger } from '../components/GenerateReportTrigger';
import { ServiceScheduleViewerModal } from '../components/ServiceScheduleViewerModal';
import { HealthMonitorWidget } from '../components/HealthMonitorWidget';
import { Calendar } from 'lucide-react';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const Dashboard: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [requisitions, setRequisitions] = useState<CashRequisition[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedModule, setSelectedModule] = useState<string | null>(null);
  const [letterheadUrl, setLetterheadUrl] = useState<string | null>(null);
  const [isScheduleViewerOpen, setIsScheduleViewerOpen] = useState(false);
  const { profile, isHOD, isAccountant, isOpsManager, isMD } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribeSettings = onSnapshot(query(collection(db, 'settings'), limit(1)), (snapshot) => {
      if (!snapshot.empty) {
        setLetterheadUrl(snapshot.docs[0].data().letterheadUrl || null);
      }
    });
    return () => unsubscribeSettings();
  }, []);

  useEffect(() => {
    const unsubscribeTasks = onSnapshot(collection(db, 'tasks'), (snapshot) => {
      setTasks(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Task)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'tasks');
    });

    const unsubscribeReports = onSnapshot(collection(db, 'reports'), (snapshot) => {
      setReports(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Report)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'reports');
    });

    const unsubscribeClients = onSnapshot(collection(db, 'clients'), (snapshot) => {
      setClients(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Client)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'clients');
    });

    const unsubscribeUsers = onSnapshot(collection(db, 'users'), (snapshot) => {
      setUsers(snapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() } as UserProfile)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'users');
    });

    const unsubscribeReq = onSnapshot(collection(db, 'cash_requisitions'), (snapshot) => {
      setRequisitions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CashRequisition)));
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'cash_requisitions');
      setLoading(false);
    });

    let unsubscribeNotifs = () => {};
    if (profile?.uid) {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', profile.uid),
        where('read', '==', false)
      );
      unsubscribeNotifs = onSnapshot(q, (snapshot) => {
        setNotifications(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Notification)));
      }, (error) => {
        handleFirestoreError(error, OperationType.LIST, 'notifications');
      });
    }

    return () => {
      unsubscribeTasks();
      unsubscribeReports();
      unsubscribeClients();
      unsubscribeUsers();
      unsubscribeReq();
      unsubscribeNotifs();
    };
  }, [profile?.uid]);

  const stats = [
    { label: 'Active Tasks', value: tasks.filter(t => t.status !== 'completed').length, icon: Wrench, color: 'bg-blue-50 text-blue-700', path: '/tasks' },
    { label: 'Total Reports', value: reports.length, icon: FileText, color: 'bg-orange-50 text-orange-600', path: '/reports' },
    { label: 'Total Clients', value: clients.length, icon: Building2, color: 'bg-blue-50 text-blue-700', path: '/clients' },
    { label: 'Cash Requisitions', value: requisitions.length, icon: DollarSign, color: 'bg-emerald-50 text-emerald-600', path: '/cashier' },
    { label: 'Team Members', value: users.length, icon: Users, color: 'bg-purple-50 text-purple-600', path: '/users-management' },
    ...(profile?.role === USER_ROLES.ADMIN ? [{ label: 'EPM PIN Reset', value: users.filter(u => u.isPayrollLocked || u.isPinResetByAdmin).length, icon: Key, color: 'bg-indigo-50 text-indigo-700', path: '/users-management?tab=epm' }] : []),
    { label: 'Unread Alerts', value: notifications.length, icon: Bell, color: 'bg-orange-50 text-orange-600', path: '#' },
  ];

  const exportToPDF = (module: string) => {
    const doc = new jsPDF() as any;
    
    // Header
    if (letterheadUrl) {
      try {
        doc.addImage(letterheadUrl, 'PNG', 0, 0, 210, 40);
      } catch (e) {
        doc.setFontSize(20);
        doc.text(`${module} Report`, 14, 22);
      }
    } else {
      doc.setFontSize(20);
      doc.text(`${module} Report`, 14, 22);
    }
    
    const startY = letterheadUrl ? 50 : 30;

    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, startY);

    let tableData: any[] = [];
    let columns: string[] = [];

    if (module === 'Active Tasks') {
      columns = ['Title', 'Client', 'Status', 'Date'];
      tableData = tasks.filter(t => t.status !== 'completed').map(t => [t.title, t.clientName || 'N/A', t.status, t.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A']);
    } else if (module === 'Total Reports') {
      columns = ['Type', 'Client', 'Technician', 'Status', 'Date'];
      tableData = reports.map(r => [r.type, r.clientName || 'N/A', r.technicianName || 'N/A', r.status, r.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A']);
    } else if (module === 'Total Clients') {
      columns = ['Name', 'Contact', 'Email', 'Phone'];
      tableData = clients.map(c => [c.name, c.contactPerson || 'N/A', c.email || 'N/A', c.phone || 'N/A']);
    } else if (module === 'Team Members') {
      columns = ['Name', 'Email', 'Role'];
      tableData = users.map(u => [u.displayName || 'N/A', u.email, ROLE_LABELS[u.role as keyof typeof ROLE_LABELS] || u.role]);
    }

    autoTable(doc, {
      startY: startY + 10,
      head: [columns],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: '#1e40af', textColor: 255 },
    });

    downloadFile(doc.output('blob'), `${module.toLowerCase().replace(' ', '_')}_report.pdf`);
  };

  const exportToExcel = (module: string) => {
    let data: any[] = [];
    if (module === 'Active Tasks') {
      data = tasks.filter(t => t.status !== 'completed').map(t => ({
        Title: t.title,
        Client: t.clientName || 'N/A',
        Status: t.status,
        Date: t.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'
      }));
    } else if (module === 'Total Reports') {
      data = reports.map(r => ({
        Type: r.type,
        Client: r.clientName || 'N/A',
        Technician: r.technicianName || 'N/A',
        Status: r.status,
        Date: r.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'
      }));
    } else if (module === 'Total Clients') {
      data = clients.map(c => ({
        Name: c.name,
        Contact: c.contactPerson || 'N/A',
        Email: c.email || 'N/A',
        Phone: c.phone || 'N/A'
      }));
    } else if (module === 'Team Members') {
      data = users.map(u => ({
        Name: u.displayName || 'N/A',
        Email: u.email,
        Role: ROLE_LABELS[u.role as keyof typeof ROLE_LABELS] || u.role
      }));
    }

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    downloadFile(blob, `${module.toLowerCase().replace(' ', '_')}_report.xlsx`);
  };

  const statusData = [
    { name: 'Pending', value: tasks.filter(t => t.status === 'pending').length },
    { name: 'In Progress', value: tasks.filter(t => t.status === 'in-progress').length },
    { name: 'Completed', value: tasks.filter(t => t.status === 'completed').length },
  ];

  const COLORS = ['#94a3b8', '#1e40af', '#f97316'];

  const recentTasks = tasks.slice(0, 5);

  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [approvingReq, setApprovingReq] = useState<CashRequisition | null>(null);
  const [approvalStatus, setApprovalStatus] = useState<'approved' | 'rejected'>('approved');
  const [approvalComment, setApprovalComment] = useState('');
  const [rejectingReq, setRejectingReq] = useState<CashRequisition | null>(null);
  const [rejectionComment, setRejectionComment] = useState('');

  const handleApproveReq = (req: CashRequisition, status: 'approved' | 'rejected') => {
    setApprovingReq(req);
    setApprovalStatus(status);
    setApprovalComment('');
    setIsApprovalModalOpen(true);
  };

  const handleConfirmApproval = async () => {
    if (!approvingReq) return;
    
    let nextStatus = approvingReq.status;
    const nextStep = {
      step: '',
      approverId: profile?.uid || '',
      approverName: profile?.displayName || '',
      status: approvalStatus,
      comment: approvalComment,
      date: Timestamp.now()
    };

    if (approvalStatus === 'rejected') {
      nextStatus = 'rejected';
      nextStep.step = approvingReq.status.replace('pending_', '').toUpperCase();
    } else {
      if (approvingReq.status === 'pending_hod') {
        nextStatus = 'pending_accountant';
        nextStep.step = 'HOD';
      } else if (approvingReq.status === 'pending_accountant') {
        nextStatus = 'pending_ops_manager';
        nextStep.step = 'Accountant';
      } else if (approvingReq.status === 'pending_ops_manager') {
        nextStatus = 'pending_md';
        nextStep.step = 'Ops Manager';
      } else if (approvingReq.status === 'pending_md') {
        nextStatus = 'approved';
        nextStep.step = 'MD';
      }
    }

    try {
      await updateDoc(doc(db, 'cash_requisitions', approvingReq.id), {
        status: nextStatus,
        approvals: [...(approvingReq.approvals || []), nextStep],
        updatedAt: Timestamp.now()
      });
      toast.success(`Requisition ${approvalStatus}`);
      setIsApprovalModalOpen(false);
      setApprovingReq(null);
    } catch (error) {
      toast.error(`Failed to ${approvalStatus} requisition`);
    }
  };

  const pendingApprovals = requisitions.filter(req => {
    if (isMD && req.status === 'pending_md') return true;
    if (isOpsManager && req.status === 'pending_ops_manager') return true;
    if (isAccountant && req.status === 'pending_accountant') return true;
    if (isHOD && req.status === 'pending_hod') {
      // If admin, they see all HOD pending
      if (profile?.role === USER_ROLES.ADMIN) return true;
      // Otherwise, only see their department
      return (profile?.hodDepartments?.includes(req.department || '') ?? false);
    }
    return false;
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0,
    }).format(amount).replace('TZS', 'TSh');
  };

  const getCurrentFiscalQuarter = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth(); // 0-11
    
    let qStart, qEnd, qName;
    if (month >= 0 && month <= 2) {
      qStart = new Date(year, 0, 1);
      qEnd = new Date(year, 2, 31, 23, 59, 59);
      qName = `Q1 ${year}`;
    } else if (month >= 3 && month <= 5) {
      qStart = new Date(year, 3, 1);
      qEnd = new Date(year, 5, 30, 23, 59, 59);
      qName = `Q2 ${year}`;
    } else if (month >= 6 && month <= 8) {
      qStart = new Date(year, 6, 1);
      qEnd = new Date(year, 8, 30, 23, 59, 59);
      qName = `Q3 ${year}`;
    } else {
      qStart = new Date(year, 9, 1);
      qEnd = new Date(year, 11, 31, 23, 59, 59);
      qName = `Q4 ${year}`;
    }
    return { qStart, qEnd, qName };
  };

  const getQuarterContractStats = () => {
    const { qStart, qName } = getCurrentFiscalQuarter();
    const now = new Date();
    
    // Filter clients who have/had contracts that span into or end in the current quarter
    const quarterClients = clients.filter(c => {
      if (!c.contractEndDate) return false;
      const endDate = new Date(c.contractEndDate + 'T23:59:59');
      // Must not have expired before the quarter started
      return endDate >= qStart;
    });

    let active = 0;
    let nearing = 0;
    let expiredInQuarter = 0;

    quarterClients.forEach(c => {
      if (!c.contractEndDate) return;
      const end = new Date(c.contractEndDate + 'T23:59:59');
      const diffMs = end.getTime() - now.getTime();
      const daysRemaining = Math.ceil(diffMs / (1000 * 3600 * 24));

      if (daysRemaining <= 0) {
        expiredInQuarter++;
      } else if (daysRemaining <= 90) {
        nearing++;
      } else {
        active++;
      }
    });

    const totalActiveAndNearing = active + nearing;
    const activePercent = totalActiveAndNearing > 0 ? Math.round((active / totalActiveAndNearing) * 100) : 0;
    const nearingPercent = totalActiveAndNearing > 0 ? Math.round((nearing / totalActiveAndNearing) * 100) : 0;

    return {
      qName,
      active,
      nearing,
      expiredInQuarter,
      total: totalActiveAndNearing,
      activePercent,
      nearingPercent
    };
  };

  if (loading) return <div className="flex items-center justify-center h-64">Loading dashboard...</div>;

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500">Welcome back, {profile?.displayName || 'User'}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <GenerateReportTrigger />
          <button
            onClick={() => setIsScheduleViewerOpen(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-indigo-900 text-white rounded-xl font-bold hover:bg-indigo-800 transition-all shadow-md shadow-indigo-900/20 active:scale-95 text-xs uppercase tracking-wider cursor-pointer"
          >
            <Calendar className="w-4 h-4 text-indigo-300" />
            <span>View Service Schedule</span>
          </button>
          <div className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-600">
            {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
        </div>
      </div>

      <HealthMonitorWidget />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex flex-col"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={cn("w-12 h-12 rounded-2xl flex items-center justify-center", stat.color)}>
                <stat.icon className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-black text-blue-900">{stat.value}</h3>
            </div>
            <p className="text-sm font-bold text-slate-500 uppercase tracking-widest">{stat.label}</p>
            
            <div className="mt-6 grid grid-cols-2 gap-2">
              <button 
                onClick={() => setSelectedModule(stat.label)}
                className="flex items-center justify-center gap-1.5 py-2 px-3 bg-blue-50 text-blue-700 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-100 transition-colors cursor-pointer"
              >
                <Eye className="w-3 h-3" />
                Info
              </button>
              <button 
                onClick={() => navigate(stat.path)}
                className="flex items-center justify-center gap-1.5 py-2 px-3 bg-orange-50 text-orange-600 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-orange-100 transition-colors cursor-pointer"
              >
                <Settings className="w-3 h-3" />
                Manage
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {selectedModule && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedModule(null)}
              className="absolute inset-0 bg-blue-900/40 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-4xl bg-white rounded-[40px] shadow-2xl p-10 overflow-hidden max-h-[90vh] flex flex-col"
            >
              <div className="flex items-start justify-between mb-8">
                <div>
                  <h3 className="text-3xl font-black text-blue-900 tracking-tight">{selectedModule}</h3>
                  <p className="text-sm text-slate-500 font-medium">Detailed information and export options</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 p-1.5 bg-slate-50 rounded-2xl border border-slate-100">
                    <button 
                      onClick={() => exportToPDF(selectedModule)}
                      className="p-3 bg-blue-900 text-white rounded-xl shadow-lg shadow-blue-900/20 hover:bg-blue-800 transition-all flex items-center gap-2"
                      title="Export to PDF"
                    >
                      <FileDown className="w-5 h-5" />
                      <span className="text-[10px] font-black uppercase tracking-widest hidden sm:inline">PDF</span>
                    </button>
                    <button 
                      onClick={() => exportToExcel(selectedModule)}
                      className="p-3 bg-orange-500 text-white rounded-xl shadow-lg shadow-orange-500/20 hover:bg-orange-600 transition-all flex items-center gap-2"
                      title="Export to Excel"
                    >
                      <FileBarChart className="w-5 h-5" />
                      <span className="text-[10px] font-black uppercase tracking-widest hidden sm:inline">Excel</span>
                    </button>
                  </div>
                  <button 
                    onClick={() => setSelectedModule(null)}
                    className="p-3 hover:bg-slate-100 rounded-2xl transition-colors"
                  >
                    <XIcon className="w-6 h-6 text-slate-400" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
                <div className="bg-slate-50 rounded-[32px] border border-slate-100 overflow-hidden overflow-x-auto">
                  <table className="w-full text-left border-collapse min-w-[600px]">
                    <thead className="bg-blue-900 text-white sticky top-0">
                      <tr>
                        {selectedModule === 'Active Tasks' && ['Title', 'Client', 'Status', 'Date'].map(h => <th key={h} className="px-6 py-4 text-[10px] font-black uppercase tracking-widest">{h}</th>)}
                        {selectedModule === 'Total Reports' && ['Type', 'Client', 'Technician', 'Status'].map(h => <th key={h} className="px-6 py-4 text-[10px] font-black uppercase tracking-widest">{h}</th>)}
                        {selectedModule === 'Total Clients' && ['Name', 'Contact', 'Email', 'Phone'].map(h => <th key={h} className="px-6 py-4 text-[10px] font-black uppercase tracking-widest">{h}</th>)}
                        {selectedModule === 'Cash Requisitions' && ['Code', 'Site', 'Amount', 'Status'].map(h => <th key={h} className="px-6 py-4 text-[10px] font-black uppercase tracking-widest">{h}</th>)}
                        {selectedModule === 'Team Members' && ['Name', 'Email', 'Role'].map(h => <th key={h} className="px-6 py-4 text-[10px] font-black uppercase tracking-widest">{h}</th>)}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {selectedModule === 'Active Tasks' && tasks.filter(t => t.status !== 'completed').map(t => (
                        <tr key={t.id} className="hover:bg-white transition-colors">
                          <td className="px-6 py-4 text-sm font-bold text-slate-900">{t.title}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">{t.clientName || 'N/A'}{t.branchName && ` - ${t.branchName}`}</td>
                          <td className="px-6 py-4">
                            <span className={cn(
                              "px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest",
                              t.status === 'in-progress' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                            )}>{t.status}</span>
                          </td>
                          <td className="px-6 py-4 text-xs text-slate-400 font-bold">{t.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}</td>
                        </tr>
                      ))}
                      {selectedModule === 'Total Reports' && reports.map(r => (
                        <tr key={r.id} className="hover:bg-white transition-colors">
                          <td className="px-6 py-4 text-sm font-bold text-slate-900">{r.type}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">{r.clientName || 'N/A'}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">{r.technicianName || 'N/A'}</td>
                          <td className="px-6 py-4">
                            <span className={cn(
                              "px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest",
                              r.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'
                            )}>{r.status}</span>
                          </td>
                        </tr>
                      ))}
                      {selectedModule === 'Total Clients' && clients.map(c => (
                        <tr key={c.id} className="hover:bg-white transition-colors">
                          <td className="px-6 py-4 text-sm font-bold text-slate-900">{c.name}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">{c.contactPerson || 'N/A'}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">{c.email || 'N/A'}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">{c.phone || 'N/A'}</td>
                        </tr>
                      ))}
                      {selectedModule === 'Cash Requisitions' && requisitions.map(req => (
                        <tr key={req.id} className="hover:bg-white transition-colors">
                          <td className="px-6 py-4 text-sm font-bold text-slate-900">{req.codeNo}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">{req.siteName}</td>
                          <td className="px-6 py-4 text-sm font-bold text-emerald-600">TZS {req.totalAmount.toLocaleString()}</td>
                          <td className="px-6 py-4">
                            <span className={cn(
                              "px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest",
                              req.status === 'released' ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'
                            )}>{req.status.replace('_', ' ')}</span>
                          </td>
                        </tr>
                      ))}
                      {selectedModule === 'Team Members' && users.map(u => (
                        <tr key={u.uid} className="hover:bg-white transition-colors">
                          <td className="px-6 py-4 text-sm font-bold text-slate-900">{u.displayName || 'N/A'}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">{u.email}</td>
                          <td className="px-6 py-4 text-sm font-black text-orange-500 uppercase tracking-widest text-[10px]">{ROLE_LABELS[u.role as keyof typeof ROLE_LABELS] || u.role}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-slate-50 flex items-center justify-between">
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.2em]">
                  Showing all records
                </p>
                <button 
                  onClick={() => navigate(stats.find(s => s.label === selectedModule)?.path || '/')}
                  className="text-blue-900 font-black text-xs uppercase tracking-widest hover:underline"
                >
                  Manage All Data →
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {/* Quarterly Contract Health Progress Bar Widget */}
          {(() => {
            const contractStats = getQuarterContractStats();
            return (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white p-8 rounded-[32px] border border-slate-100 shadow-sm space-y-6"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">Quarterly Contract Health</h3>
                    <p className="text-xs text-slate-500 font-medium">
                      Fiscal Quarter: <span className="font-bold text-blue-900">{contractStats.qName}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-4 text-xs font-bold uppercase tracking-wider">
                    <span className="flex items-center gap-1.5 text-emerald-600">
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                      Active: {contractStats.activePercent}%
                    </span>
                    <span className="flex items-center gap-1.5 text-amber-600">
                      <span className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse" />
                      Nearing Expiry: {contractStats.nearingPercent}%
                    </span>
                  </div>
                </div>

                {/* Progress Bar Track */}
                <div className="relative w-full h-4 bg-slate-100 rounded-full overflow-hidden flex">
                  {contractStats.total > 0 ? (
                    <>
                      <div 
                        style={{ width: `${contractStats.activePercent}%` }} 
                        className="bg-emerald-500 h-full transition-all duration-500"
                        title={`Active: ${contractStats.active} contracts (${contractStats.activePercent}%)`}
                      />
                      <div 
                        style={{ width: `${contractStats.nearingPercent}%` }} 
                        className="bg-amber-500 h-full transition-all duration-500"
                        title={`Nearing Expiration: ${contractStats.nearing} contracts (${contractStats.nearingPercent}%)`}
                      />
                    </>
                  ) : (
                    <div className="w-full h-full bg-slate-100 flex items-center justify-center text-[10px] text-slate-400 font-bold uppercase">
                      No contracts tracked for this quarter
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                  <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Fully Active</span>
                    <span className="text-xl font-black text-emerald-600 mt-1 block">
                      {contractStats.active} <span className="text-xs font-bold text-slate-400">Contracts</span>
                    </span>
                    <span className="text-[10px] text-slate-400 block mt-1 font-medium">Expiring in &gt; 90 days</span>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Nearing Expiry</span>
                    <span className="text-xl font-black text-amber-600 mt-1 block">
                      {contractStats.nearing} <span className="text-xs font-bold text-slate-400">Contracts</span>
                    </span>
                    <span className="text-[10px] text-slate-400 block mt-1 font-medium">Expiring within 90 days</span>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Total Tracked</span>
                    <span className="text-xl font-black text-blue-900 mt-1 block">
                      {contractStats.total} <span className="text-xs font-bold text-slate-400">Contracts</span>
                    </span>
                    <span className="text-[10px] text-slate-400 block mt-1 font-medium">Spanning current quarter</span>
                  </div>
                </div>
              </motion.div>
            );
          })()}

          {pendingApprovals.length > 0 && (
            <div className="bg-white p-8 rounded-[32px] border border-slate-100 shadow-sm">
              <h3 className="text-xl font-black text-slate-900 mb-6 flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-orange-500" />
                Pending Approvals
                <span className="px-2.5 py-0.5 bg-orange-100 text-orange-600 text-xs font-black rounded-full">
                  {pendingApprovals.length}
                </span>
              </h3>
              <div className="space-y-4">
                {pendingApprovals.map((req) => (
                  <div key={req.id} className="bg-slate-50 p-6 rounded-3xl border border-transparent hover:border-blue-900/10 transition-all flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <h4 className="font-black text-blue-900">{req.codeNo}</h4>
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[8px] font-black uppercase tracking-widest rounded">
                          {req.status.replace('pending_', '').replace('_', ' ')}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 font-medium mb-2">{req.siteName} - {req.requestedBy} ({req.department})</p>
                      <p className="text-lg font-black text-slate-900">{formatCurrency(req.totalAmount)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleApproveReq(req, 'rejected')}
                        className="p-3 bg-white text-red-500 rounded-xl border border-slate-200 hover:bg-red-50 transition-all"
                        title="Reject"
                      >
                        <X className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleApproveReq(req, 'approved')}
                        className="flex items-center gap-2 px-6 py-3 bg-blue-900 text-white rounded-xl font-black uppercase tracking-widest text-[10px] hover:bg-blue-800 transition-all shadow-lg shadow-blue-900/20"
                      >
                        <Check className="w-4 h-4" /> Approve
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm">
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-lg font-bold text-slate-900">Task Status Distribution</h3>
              <TrendingUp className="text-slate-400 w-5 h-5" />
            </div>
            <div className="h-80 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={statusData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                  <Tooltip 
                    cursor={{ fill: '#f8fafc' }}
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                  />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm">
          <h3 className="text-lg font-bold text-slate-900 mb-6">Recent Activity</h3>
          <div className="space-y-6">
            {recentTasks.length > 0 ? recentTasks.map((task, i) => (
              <div key={task.id} className="flex gap-4">
                <div className={cn(
                  "w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0",
                  task.status === 'completed' ? 'bg-emerald-50 text-emerald-600' : 
                  task.status === 'in-progress' ? 'bg-blue-50 text-blue-600' : 'bg-slate-50 text-slate-400'
                )}>
                  {task.status === 'completed' ? <CheckCircle2 className="w-5 h-5" /> : 
                   task.status === 'in-progress' ? <AlertCircle className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{task.title}</p>
                  <p className="text-xs text-slate-500 truncate">{task.clientName}{task.branchName && ` - ${task.branchName}`}</p>
                  <p className="text-[10px] text-slate-400 mt-1">
                    {task.updatedAt?.toDate?.() ? task.updatedAt.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                  </p>
                </div>
              </div>
            )) : (
              <div className="text-center py-10 text-slate-400 italic text-sm">
                No recent activity
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Approval/Rejection Modal */}
      <AnimatePresence>
        {isApprovalModalOpen && approvingReq && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsApprovalModalOpen(false)}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl p-8"
            >
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                    approvalStatus === 'approved' ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"
                  }`}>
                    {approvalStatus === 'approved' ? <CheckCircle2 className="w-6 h-6" /> : <AlertCircle className="w-6 h-6" />}
                  </div>
                  <div>
                    <h2 className="text-2xl font-black text-slate-900 tracking-tight">
                      {approvalStatus === 'approved' ? 'Approve' : 'Reject'} Requisition
                    </h2>
                    <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">{approvingReq.codeNo}</p>
                  </div>
                </div>
                <button onClick={() => setIsApprovalModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                  <XIcon className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1">
                    {approvalStatus === 'approved' ? 'Approval' : 'Rejection'} Comment
                  </label>
                  <textarea
                    value={approvalComment}
                    onChange={(e) => setApprovalComment(e.target.value)}
                    placeholder={`Enter your ${approvalStatus === 'approved' ? 'approval' : 'rejection'} reason...`}
                    rows={4}
                    className="w-full px-5 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-900 transition-all font-medium resize-none"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setIsApprovalModalOpen(false)}
                    className="flex-1 py-4 bg-slate-100 text-slate-600 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-slate-200 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmApproval}
                    className={`flex-[2] py-4 text-white rounded-2xl font-black uppercase tracking-widest text-xs transition-all shadow-lg ${
                      approvalStatus === 'approved' 
                        ? "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/20" 
                        : "bg-red-600 hover:bg-red-700 shadow-red-600/20"
                    }`}
                  >
                    Confirm {approvalStatus === 'approved' ? 'Approval' : 'Rejection'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <ServiceScheduleViewerModal
        isOpen={isScheduleViewerOpen}
        onClose={() => setIsScheduleViewerOpen(false)}
      />
    </div>
  );
};

export default Dashboard;
